import type { WatsonChainProduct, WatsonChainProductsResponse, WatsonLedgerRow, WatsonProductData } from '../types/watsonChain'

export const ONECHAIN_SCAN_TX_BASE_URL = 'https://onechainscan.io/transaction'

export const getOneChainScanTxUrl = (txId: string) => {
  const normalized = String(txId || '').trim().replace(/^0x/i, '')
  return `${ONECHAIN_SCAN_TX_BASE_URL}/${normalized}`
}

const parseProductData = (raw: unknown): WatsonProductData | null => {
  if (!raw) return null

  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as WatsonProductData
    } catch {
      return null
    }
  }

  if (typeof raw === 'object') {
    return raw as WatsonProductData
  }

  return null
}

export const normalizeWatsonProduct = (product: WatsonChainProduct): WatsonLedgerRow | null => {
  const data = parseProductData(product.data)
  if (!data) return null

  const bottles = Array.isArray(data.bottleRecords) ? data.bottleRecords : []
  const primaryBottle = bottles[0]
  const weightGrams = bottles.reduce((sum, bottle) => sum + (Number(bottle.weight) || 0), 0)
  const points = Number(data.point) || bottles.reduce((sum, bottle) => sum + (Number(bottle.point) || 0), 0)
  const credits = Number(data.value) || points
  const recordedAtMs = Number(data.time) || Number(primaryBottle?.time) || 0

  let status: WatsonLedgerRow['status'] = 'pending'
  if (Number(data.success) === 1 && Number(data.effective) === 1) {
    status = 'verified'
  } else if (Number(data.success) === 0) {
    status = 'failed'
  }

  return {
    id: product.id,
    txId: product.txId || null,
    recordedAt: recordedAtMs > 0 ? new Date(recordedAtMs).toISOString() : '',
    recordedAtMs,
    machineId: Number(data.mid) || Number(primaryBottle?.mid) || 0,
    brand: primaryBottle?.brand || 'Unknown',
    barcode: primaryBottle?.barcode ?? null,
    capacityMl: primaryBottle?.capacity ?? null,
    bottleCount: Number(data.total) || bottles.length,
    weightGrams,
    points,
    credits,
    status,
    platformNumber: data.platformNumber ? String(data.platformNumber) : null
  }
}

export const normalizeWatsonProducts = (
  products: WatsonChainProduct[],
  options?: { preserveOrder?: boolean }
): WatsonLedgerRow[] => {
  const rows = products
    .map(normalizeWatsonProduct)
    .filter((row): row is WatsonLedgerRow => Boolean(row))

  if (options?.preserveOrder) {
    return rows
  }

  return rows.sort((a, b) => b.recordedAtMs - a.recordedAtMs)
}

interface WatsonChainApiPayload {
  source: 'onechain' | 'fallback'
  fetchedAt: string
  count: number
  warning?: string
  products: WatsonChainProduct[]
  pagination?: {
    method: string
    pageSize: number
    bookmark: string
    newBookmark: string
    hasMore: boolean
    pagesFetched: number
  }
}

export const fetchWatsonChainRecords = async (options?: {
  refresh?: boolean
  paginated?: boolean
  pageSize?: number
  bookmark?: string
}): Promise<WatsonChainProductsResponse> => {
  const params = new URLSearchParams()
  if (options?.refresh) params.set('refresh', '1')
  if (options?.paginated) {
    params.set('paginated', '1')
    params.set('pageSize', String(options.pageSize || 10))
    params.set('bookmark', options.bookmark || '')
  }
  const query = params.toString()
  const response = await fetch(`/api/chain/products/watson${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' }
  })

  const bodyText = await response.text()
  if (!response.ok) {
    throw new Error(`Failed to load Watson chain records (${response.status})`)
  }

  const json = JSON.parse(bodyText) as WatsonChainApiPayload
  if (!Array.isArray(json.products)) {
    throw new Error('Invalid Watson chain records response')
  }

  return {
    source: json.source,
    fetchedAt: json.fetchedAt,
    count: json.count,
    warning: json.warning,
    records: normalizeWatsonProducts(json.products, { preserveOrder: Boolean(options?.paginated) }),
    pagination: json.pagination
  }
}
